import { useMemo, useState } from 'react';
import { Plus, Search, ChevronRight } from 'lucide-react';
import { useI18n } from '../../context/useI18n';
import { buildGymOrgTree, collectOrgLicenses } from '../../utils/organizationView';
import { findOrgOperator, formatDateLabel, formatDaysRemaining, summarizeSubscription } from '../../utils/organizationLicenseSummary';
import CreateVirtualGymModal from './CreateVirtualGymModal';
import ManageGymOrgModal from './ManageGymOrgModal';
import OrgEndUsersVigenciasModal from './OrgEndUsersVigenciasModal';

function LicenseChips({ operator }) {
  const licenses = collectOrgLicenses(operator);
  if (!licenses.length) {
    return <span className="text-xs text-stone-400">Sin licencias</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {licenses.map((lic) => (
        <span
          key={`${lic.label}-${lic.code}`}
          className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-900"
        >
          {lic.label}
        </span>
      ))}
    </div>
  );
}

export default function OrganizationsD28dTree({
  gyms = [],
  users = [],
  onReload,
  onOpenGymPanel,
  onExtendOperator,
  onManageLicenses,
  onExtendEndUser,
  extendingEndUser = false,
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createSedeUnder, setCreateSedeUnder] = useState(null);
  const [manageTarget, setManageTarget] = useState(null);
  const [vigenciasGym, setVigenciasGym] = useState(null);

  const tree = useMemo(() => buildGymOrgTree(gyms), [gyms]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tree;
    return tree
      .map((node) => {
        const rootMatch = node.name.toLowerCase().includes(q);
        const sedes = node.sedes.filter((s) => s.name.toLowerCase().includes(q));
        if (rootMatch || sedes.length) {
          return { ...node, sedes: rootMatch ? node.sedes : sedes };
        }
        return null;
      })
      .filter(Boolean);
  }, [tree, search]);

  const resolveOperator = (gymId) => findOrgOperator(users, { gymId });

  const openManage = (gym, orgName) => {
    const operator = findOrgOperator(users, { gymId: gym.id });
    setManageTarget({ gym, operator, orgName });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-stone-600">
          {t('orgs.gym_tree_hint', 'Organización principal y sedes. Cada sede opera de forma independiente.')}
        </p>
        <button type="button" className="btn-primary text-sm inline-flex items-center gap-2 shrink-0" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4" />
          {t('orgs.create_gym', 'Crear gimnasio virtual')}
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-stone-500 absolute left-2 top-2.5" />
        <input
          className="input pl-7 w-full"
          placeholder={t('common.search', 'Buscar')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 ui-light-panel">
        {filtered.length === 0 ? (
          <p className="p-6 text-sm text-stone-500 text-center">{t('orgs.no_gyms', 'Sin gimnasios virtuales activos.')}</p>
        ) : filtered.map((node) => {
          const rootOp = resolveOperator(node.gym.id);
          const rootSub = summarizeSubscription(rootOp?.subscription);
          return (
            <div key={node.gym.id} className="p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-stone-900">{node.name}</p>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {t('orgs.type.virtual_gym', 'Gimnasio virtual')}
                    {rootOp?.email ? ` · ${rootOp.email}` : ''}
                  </p>
                  <div className="mt-2">
                    <p className="text-[10px] uppercase tracking-wide text-stone-400 mb-1">{t('orgs.licenses', 'Licencias')}</p>
                    <LicenseChips operator={rootOp} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary text-xs" onClick={() => openManage(node.gym, node.name)}>
                    {t('orgs.manage', 'Gestionar')}
                  </button>
                  {onOpenGymPanel ? (
                    <button type="button" className="btn-secondary text-xs" onClick={() => onOpenGymPanel({ id: node.gym.id, name: node.name })}>
                      {t('orgs.open_d28d_panel', 'Panel D28D')}
                    </button>
                  ) : null}
                  {rootOp && onManageLicenses ? (
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      onClick={() => onManageLicenses({
                        operator: rootOp,
                        orgName: node.name,
                      })}
                    >
                      {t('orgs.licenses', 'Licencias')}
                    </button>
                  ) : null}
                  {onExtendEndUser ? (
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      onClick={() => setVigenciasGym(node.gym)}
                    >
                      {t('vigencias.end_users_short', 'Vigencias usuarios')}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 ml-0 sm:ml-1">
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => setCreateSedeUnder({ id: node.gym.id, name: node.name })}
                >
                  {t('orgs.add_sede', 'Agregar sede')}
                </button>
              </div>

              {node.sedes.length > 0 ? (
                <ul className="ml-2 border-l-2 border-stone-200 pl-4 space-y-3">
                  {node.sedes.map((sede) => {
                    const sedeOp = resolveOperator(sede.gym.id);
                    const sedeSub = summarizeSubscription(sedeOp?.subscription);
                    return (
                      <li key={sede.gym.id} className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0">
                          <ChevronRight className="w-4 h-4 text-stone-400 shrink-0 mt-0.5" aria-hidden />
                          <div>
                            <p className="font-medium text-stone-800 text-sm">{sede.name}</p>
                            <p className="text-xs text-stone-500">{t('orgs.type.sede', 'Sede')}</p>
                            <div className="mt-1">
                              <LicenseChips operator={sedeOp} />
                            </div>
                            {sedeSub.validUntil ? (
                              <p className="text-[10px] text-stone-500 mt-1">
                                {formatDateLabel(sedeSub.validUntil)} · {formatDaysRemaining(sedeSub.daysLeft)}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" className="btn-secondary text-xs" onClick={() => openManage(sede.gym, sede.name)}>
                            {t('orgs.manage', 'Gestionar')}
                          </button>
                          {onOpenGymPanel ? (
                            <button type="button" className="btn-secondary text-xs" onClick={() => onOpenGymPanel({ id: sede.gym.id, name: sede.name })}>
                              {t('orgs.open_d28d_panel', 'Panel D28D')}
                            </button>
                          ) : null}
                          {sedeOp && onManageLicenses ? (
                            <button
                              type="button"
                              className="btn-secondary text-xs"
                              onClick={() => onManageLicenses({
                                operator: sedeOp,
                                orgName: sede.name,
                              })}
                            >
                              {t('orgs.licenses', 'Licencias')}
                            </button>
                          ) : null}
                          {onExtendEndUser ? (
                            <button
                              type="button"
                              className="btn-secondary text-xs"
                              onClick={() => setVigenciasGym(sede.gym)}
                            >
                              {t('vigencias.end_users_short', 'Vigencias usuarios')}
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-xs text-stone-500 ml-4">{t('orgs.no_sedes', 'Aún no hay sedes. Usa «Agregar sede» para crear una.')}</p>
              )}
            </div>
          );
        })}
      </div>

      <CreateVirtualGymModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          onReload?.();
        }}
      />

      <CreateVirtualGymModal
        open={Boolean(createSedeUnder)}
        parentGym={createSedeUnder}
        onClose={() => setCreateSedeUnder(null)}
        onCreated={() => {
          setCreateSedeUnder(null);
          onReload?.();
        }}
      />

      <OrgEndUsersVigenciasModal
        open={Boolean(vigenciasGym)}
        gym={vigenciasGym}
        users={users}
        onClose={() => setVigenciasGym(null)}
        onExtend={onExtendEndUser}
        extending={extendingEndUser}
      />

      <ManageGymOrgModal
        gym={manageTarget?.gym}
        operator={manageTarget?.operator}
        open={Boolean(manageTarget)}
        onClose={() => setManageTarget(null)}
        onSaved={() => onReload?.()}
        onArchived={() => {
          setManageTarget(null);
          onReload?.();
        }}
        onManageLicenses={(target) => {
          setManageTarget(null);
          onManageLicenses?.(target);
        }}
        onOpenGymPanel={onOpenGymPanel}
      />
    </div>
  );
}
